import { combineReducers, configureStore, type EnhancedStore } from '@reduxjs/toolkit';
// eslint-disable-next-line no-restricted-imports
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import analyticsMiddleware from '@common/store/config/analyticsMiddleware';
import rootReducer from '@common/store/reducers';
import featureFlagsReducer from '@common/store/reducers/featureFlags';
import graphsReducer, {
  restoreSelectedRegisterDataSeriesGraphAction,
} from '@common/store/reducers/graphs';
import { type InstallationId } from '@common/types/brandedTypes';
import { type SelectedRegisterDataSeriesGraph } from '@common/types/models/SelectedDataPoints';
import { type StoreState, UserState } from '@common/types/store';
import localStorageManager from '@common/utils/storage/localStorageManager';
import {
  LOCAL_STORAGE_SELECTED_DIAGRAM_DATAPOINTS_KEY,
  LOCAL_STORAGE_SELECTED_STATISTICS_DATAPOINTS_KEY,
} from '@common/utils/storage/storageKeys';

export enum TargetTab {
  Diagram = 'diagram',
  Statistics = 'statistics',
}

const manageDataPointsStorage = (
  store: EnhancedStore<StoreState>,
  targetTab: TargetTab,
  storageKey: string,
) => {
  store.subscribe(() => {
    const state = store.getState();
    if (state.main.userState === UserState.LoggedIn) {
      localStorageManager.saveItem(
        storageKey,
        state.graphs[targetTab].selectedRegisterDataSeriesGraphs,
      );
    } else {
      localStorageManager.saveItem(storageKey, {});
    }
  });

  store.dispatch(restoreSelectedRegisterDataSeriesGraphAction(
    {
      targetTab,
      data: localStorageManager.getItem<Record<InstallationId, SelectedRegisterDataSeriesGraph[]>>(
        storageKey,
      ) ?? {},
    },
  ));
};

export const createStore = () => {
  const store = configureStore({
    reducer: combineReducers({
      main: rootReducer,
      featureFlags: featureFlagsReducer,
      graphs: graphsReducer,
    }),
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        serializableCheck: false,
      });
      if (process.env.POSTHOG_TOKEN) {
        middleware.push(analyticsMiddleware);
      }
      return middleware;
    },
    devTools: DEBUG,
  });

  manageDataPointsStorage(
    store,
    TargetTab.Diagram,
    LOCAL_STORAGE_SELECTED_DIAGRAM_DATAPOINTS_KEY,
  );
  manageDataPointsStorage(
    store,
    TargetTab.Statistics,
    LOCAL_STORAGE_SELECTED_STATISTICS_DATAPOINTS_KEY,
  );

  return store;
};

export const useAppSelector: TypedUseSelectorHook<StoreState> = useSelector;

const store = createStore();
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export default store;
